import qs from 'qs';

import { ALL_UUID } from '@/shared/constants';
import { routes } from '@/shared/routes';
import {
  DueTimeFrame,
  DueTimeFrameType,
  OrganisationType,
  RecordsStatus,
  RecurringTaskStatus,
  TaskStatus,
} from '@/shared/types/enum';
import { formatIsoDate } from '@/shared/utils/date';
import { merge } from '@/shared/utils/object';
import { utcSubDays, utcToday } from '@/shared/utils/utc';

import { OrganisationView, RecurringTaskView, SmartTaskView, TaskView } from '@prisma/client';

export const COMPANIES_HOUSE_URL_TEMPLATE =
  'https://find-and-update.company-information.service.gov.uk/company/${value}';

export const CHARITY_COMMISSION_URL_TEMPLATE =
  'https://register-of-charities.charitycommission.gov.uk/charity-details/?regId=${value}&subId=0';

export const OSCR_URL_TEMPLATE =
  'https://www.oscr.org.uk/about-charities/search-the-register/charity-details?number=${value}';

export const CHARITY_COMMISSION_NI_URL_TEMPLATE =
  'https://www.charitycommissionni.org.uk/charity-search/charity-details-page/?regId=${value}&subId=0';

export const COMPANY_URL_TEMPLATE = routes.organisations.edit({ id: '${value}' });
export const INDIVIDUAL_URL_TEMPLATE = routes.individuals.edit({ id: '${value}' });

export const appendQuery = (url: string, query: Record<string, string | undefined>, encode?: boolean) => {
  const queryString = qs.stringify(query, { encode: encode !== undefined ? encode : true });
  return queryString ? `${url}${url.indexOf('?') === -1 ? '?' : '&'}${queryString}` : url;
};

export const setHash = (url: string, hash: string | undefined): string => {
  if (!hash) return url;
  const [base] = url.split('#');
  return `${base}#${hash.replace(/^#/, '')}`;
};

export const sortedQueryString = (params: Record<string, any>) => {
  const sortedParams = Object.entries(params).sort(([a], [b]) => a.localeCompare(b));
  return new URLSearchParams(sortedParams).toString();
};

export const offcanvasUrl = (url: string, sectionId?: string) =>
  setHash(appendQuery(url, { offcanvas: 'true' }), sectionId);

export const offcanvasBackUrl = (url: string, sectionId?: string) =>
  appendQuery(url, { offcanvas: 'true', back: sectionId ?? 'true' });

export const companiesHouseUrl = (registrationNumber: string) =>
  COMPANIES_HOUSE_URL_TEMPLATE.replaceAll('${value}', registrationNumber);

export const charityRegisterLinkProps = (
  organisationType?: string | null,
  registrationNumber?: string | null,
  charityNumber?: string | null,
): { urlTemplate: string; urlLabel: string } | undefined => {
  if (
    organisationType === OrganisationType.CharitableAssociation ||
    organisationType === OrganisationType.CharitableTrust
  ) {
    const prefix = (charityNumber ?? '').trim().toUpperCase();

    if (prefix.startsWith('SC')) {
      return { urlTemplate: OSCR_URL_TEMPLATE, urlLabel: 'View In OSCR' };
    }

    return { urlTemplate: CHARITY_COMMISSION_URL_TEMPLATE, urlLabel: 'View In Charity Commission' };
  }

  if (organisationType === OrganisationType.CIO) {
    return { urlTemplate: CHARITY_COMMISSION_URL_TEMPLATE, urlLabel: 'View In Charity Commission' };
  }

  if (organisationType === OrganisationType.SCIO) {
    return { urlTemplate: OSCR_URL_TEMPLATE, urlLabel: 'View In OSCR' };
  }

  if (organisationType === OrganisationType.LimitedCompany || organisationType === OrganisationType.CharitableCompany) {
    const prefix = (registrationNumber ?? '').trim().toUpperCase();

    if (prefix.startsWith('NI')) {
      return { urlTemplate: CHARITY_COMMISSION_NI_URL_TEMPLATE, urlLabel: 'View In Charity Commission NI' };
    }

    if (prefix.startsWith('SC')) {
      return { urlTemplate: OSCR_URL_TEMPLATE, urlLabel: 'View In OSCR' };
    }

    if (organisationType === OrganisationType.CharitableCompany) {
      return { urlTemplate: CHARITY_COMMISSION_URL_TEMPLATE, urlLabel: 'View In Charity Commission' };
    }
  }
};

export const charityRegisterUrl = (
  charityNumber: string,
  organisationType?: string | null,
  registrationNumber?: string | null,
) => {
  const linkProps = charityRegisterLinkProps(organisationType, registrationNumber, charityNumber);
  return linkProps ? linkProps.urlTemplate.replaceAll('${value}', charityNumber) : undefined;
};

const filterUrl = <T>(url: string, query: Partial<Record<keyof T, string>>) =>
  appendQuery(
    url,
    Object.keys(query)
      .map((key) => {
        return { [`f_${key}`]: query[key as keyof T]! };
      })
      .reduce(merge),
    false,
  );

export const organisationsFilterUrl = (params: {
  userId?: string;
  groupCompanyId?: string;
  importFailed?: boolean;
  dueTimeFrame?: DueTimeFrame;
  dueTimeFrameType?: DueTimeFrameType;
}) => {
  const { userId, groupCompanyId, importFailed, dueTimeFrame, dueTimeFrameType } = params;

  const query: Partial<Record<keyof OrganisationView, string>> = {};

  if (userId) {
    query.accountManagerId = userId;
  }

  if (groupCompanyId) {
    query.groupId = groupCompanyId;
  }

  if (importFailed) {
    query.importFailed = 'true';
  }

  if (dueTimeFrame) {
    let dueTimeFramesField: keyof OrganisationView = 'dueTimeFrames';

    if (dueTimeFrameType === DueTimeFrameType.Accounts) {
      dueTimeFramesField = 'accountsDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.AmlCheck) {
      dueTimeFramesField = 'amlCheckDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.Bookkeeping) {
      dueTimeFramesField = 'bookkeepingDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.CisReturn) {
      dueTimeFramesField = 'cisReturnDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.ConfirmationStatement) {
      dueTimeFramesField = 'confirmationStatementDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.MtdItUpdate) {
      dueTimeFramesField = 'mtdItUpdateDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.Payroll) {
      dueTimeFramesField = 'payrollDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.TaxReturn) {
      dueTimeFramesField = 'taxReturnDueTimeFrames';
    } else if (dueTimeFrameType === DueTimeFrameType.VatReturn) {
      dueTimeFramesField = 'vatReturnDueTimeFrames';
    }

    query[dueTimeFramesField] = `contains|${dueTimeFrame}`;
  }

  return filterUrl(routes.organisations.list({ archived: false }), query);
};

export const tasksFilterUrl = (params: {
  clientId?: string;
  serviceId?: string;
  userId?: string;
  partnerId?: string;
  dueTimeFrame?: DueTimeFrame;
  dueTimeFrameField?: 'plannedStartDate' | 'plannedCompletionDate' | 'deadlineDate';
  recordsStatus?: RecordsStatus;
  status?: TaskStatus;
  preset?: 'uncompleted' | 'completed30Days' | 'notStarted' | 'started';
}) => {
  const { clientId, serviceId, userId, partnerId, dueTimeFrame, dueTimeFrameField, recordsStatus, status, preset } =
    params;

  const query: Partial<Record<keyof TaskView, string>> = {};

  if (clientId) {
    query.clientId = clientId;
  }

  if (serviceId) {
    query.serviceId = serviceId !== ALL_UUID ? serviceId : 'isnull';
  }

  if (userId) {
    query.assignedToUserId = userId;
  }

  if (partnerId) {
    query.partnerId = partnerId;
  }

  if (dueTimeFrame) {
    if (dueTimeFrameField === 'plannedStartDate') {
      query.plannedStartTimeFrames = `contains|${dueTimeFrame}`;
    } else if (dueTimeFrameField === 'plannedCompletionDate') {
      query.plannedCompletionTimeFrames = `contains|${dueTimeFrame}`;
    } else {
      query.dueTimeFrames = `contains|${dueTimeFrame}`;
    }
  }

  if (recordsStatus) {
    query.recordsStatus = recordsStatus;
  }

  if (status) {
    query.status = status;
  }

  if (preset === 'uncompleted') {
    query.status = `notequal|${TaskStatus.Completed}`;
  } else if (preset === 'completed30Days') {
    query.completedDate = `greaterthanorequal|${formatIsoDate(utcSubDays(utcToday(), 30))}`;
    query.status = TaskStatus.Completed;
  } else if (preset === 'notStarted') {
    query.startedDate = 'isnull';
  } else if (preset === 'started') {
    query.startedDate = 'isnotnull';
  }

  return filterUrl(routes.tasks.list(), query);
};

export const recurringTasksFilterUrl = (params: {
  clientId?: string;
  serviceId?: string;
  userId?: string;
  status?: RecurringTaskStatus;
}) => {
  const { clientId, serviceId, userId, status } = params;

  const query: Partial<Record<keyof RecurringTaskView, string>> = {};

  if (clientId) {
    query.clientId = clientId;
  }

  if (serviceId) {
    query.serviceId = serviceId;
  }

  if (userId) {
    query.assignedToUserId = userId;
  }

  if (status) {
    query.status = status;
  }

  return filterUrl(routes.recurringTasks.list(), query);
};

export const smartTasksFilterUrl = (params: { serviceId?: string; userId?: string }) => {
  const { serviceId, userId } = params;

  const query: Partial<Record<keyof SmartTaskView, string>> = {};

  if (serviceId) {
    query.serviceId = serviceId;
  }

  if (userId) {
    query.assignedToUserId = userId;
  }

  return filterUrl(routes.smartTasks.list(), query);
};
